import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StatusChip from '../../../../components/StatusChip';
import RowActionsMenu from '../../../../components/RowActionsMenu/RowActionsMenu';
import { LastPaymentCell, MonthChips, OwedCell } from '../Debts/DebtCells';
import { FinanceBreakdownRowDto } from '../../../../common/interfaces/invoicing.interface';
import { InvoiceStateEnum } from '../../../../common/enums/invoicing.enum';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { formatSerbianDate } from '../../../../utilities/helper/date.helper';

const GRID = 'minmax(0,1.3fr) 170px 130px minmax(0,1fr) 150px 120px 40px';

export interface ChildRowActions {
  onPay: (row: FinanceBreakdownRowDto) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onOpenContract: (childId: string) => void;
  onHistory: (row: FinanceBreakdownRowDto) => void;
}

const rowSx = { display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'start', px: 2, py: 1.5 } as const;
const numeric = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' } as const;

/**
 * A group for one month, child by child: the month's invoice and where it stands, and what
 * the child owes in all. A child enrolled in the month without an invoice is listed too -
 * that is the one the admin has to do something about.
 *
 * The debt, open invoices and last payment are the child's in full, as on Debts; for a
 * child that changed group they can include another group's invoices.
 */
const ChildrenMonthTable: React.FC<{
  rows: FinanceBreakdownRowDto[];
  monthLabel: string;
  totalLabel: string;
  actions: ChildRowActions;
}> = ({ rows, monthLabel, totalLabel, actions }) => {
  const { t } = useTranslation();
  const children = rows.filter(row => row.id && row.child);
  const invoiced = children.filter(row => row.child?.invoiceId);
  const paid = invoiced.filter(row => row.child?.invoiceState === InvoiceStateEnum.PAID);
  const amount = invoiced.reduce((sum, row) => sum + (row.child?.totalGross ?? 0), 0);
  // the column's own sum: a child's debt is the child's in full, so this can differ from the
  // group's figure above, which counts only the invoices issued in this group
  const owed = children.reduce((sum, row) => sum + (row.child?.outstanding ?? 0), 0);

  return (
    <Box
      role="table"
      aria-label={t('finance.children.ariaLabel')}
      sx={{ border: 1, borderColor: 'line', borderRadius: 4, overflow: 'hidden' }}
    >
      <Box role="row" sx={{ ...rowSx, py: 1, bgcolor: 'surface.subtle', '& > *': { fontSize: 12, color: 'text.secondary' } }}>
        <span role="columnheader">{t('finance.children.child')}</span>
        <span role="columnheader">{t('finance.children.invoice', { month: monthLabel })}</span>
        <Box component="span" role="columnheader" sx={numeric}>
          {t('finance.children.amount')}
        </Box>
        <span role="columnheader">{t('finance.children.unpaid')}</span>
        <span role="columnheader">{t('finance.locations.owed')}</span>
        <span role="columnheader">{t('finance.children.lastPayment')}</span>
        <span role="columnheader" />
      </Box>
      {!children.length && <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>{t('finance.noChildren')}</Typography>}
      {children.map(row => {
        const child = row.child!;
        const name = row.name ?? '';
        return (
          <Box
            role="row"
            key={row.id}
            tabIndex={0}
            onClick={() => actions.onPay(row)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                actions.onPay(row);
              }
            }}
            sx={{ ...rowSx, borderTop: 1, borderColor: 'line', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Box role="cell" sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{name}</Typography>
              {child.payerFullName ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{child.payerFullName}</Typography>
              ) : (
                <Chip size="small" color="warning" variant="outlined" label={t('contracts.noPayer')} sx={{ mt: 0.5 }} />
              )}
            </Box>
            <Box role="cell">
              {child.invoiceState ? (
                <>
                  <StatusChip kind="invoiceState" status={child.invoiceState} />
                  {child.invoiceNumber && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{child.invoiceNumber}</Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
                  {[
                    t('finance.children.noInvoice'),
                    child.joinedAt ? t('finance.children.joined', { date: formatSerbianDate(child.joinedAt) }) : null
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
            <Box role="cell" sx={numeric}>
              {child.totalGross !== null ? (
                <>
                  <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(child.totalGross)}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {t('finance.children.paid', { amount: formatMoney(child.paidAmount ?? 0) })}
                  </Typography>
                </>
              ) : (
                '—'
              )}
            </Box>
            <Box role="cell" sx={{ minWidth: 0 }}>
              {child.openInvoices.length || child.credit > 0 ? (
                <MonthChips invoices={child.openInvoices} credit={child.credit} />
              ) : (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('finance.children.allPaid')}</Typography>
              )}
            </Box>
            <Box role="cell">
              <OwedCell
                outstanding={child.outstanding}
                overdueAmount={child.overdueAmount}
                daysOverdue={child.daysOverdue}
                credit={child.credit}
                invoices={child.openInvoices}
              />
            </Box>
            <Box role="cell">
              <LastPaymentCell payment={child.lastPayment} dateOnly />
            </Box>
            <Box role="cell">
              <RowActionsMenu
                name={name}
                actions={[
                  { label: t('payments.record'), onClick: () => actions.onPay(row) },
                  {
                    label: t('finance.children.openInvoice'),
                    hidden: !child.invoiceId,
                    onClick: () => actions.onOpenInvoice(child.invoiceId!)
                  },
                  { label: t('balances.openContract'), onClick: () => actions.onOpenContract(row.id!) },
                  { label: t('balances.paymentHistory'), onClick: () => actions.onHistory(row) }
                ]}
              />
            </Box>
          </Box>
        );
      })}
      {children.length > 0 && (
        <Box role="row" sx={{ ...rowSx, borderTop: 1, borderColor: 'line', bgcolor: 'surface.subtle', '& *': { fontWeight: 600 } }}>
          <Box role="cell">
            <Typography sx={{ fontSize: 14 }}>{totalLabel}</Typography>
          </Box>
          <Box role="cell">
            <Typography sx={{ fontSize: 13 }}>
              {t('finance.children.invoices', { count: invoiced.length })} · {t('finance.children.paidCount', { count: paid.length })}
            </Typography>
          </Box>
          <Box role="cell" sx={numeric}>
            <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(amount)}</Typography>
          </Box>
          <span role="cell" />
          <Box role="cell">
            <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', color: owed > 0 ? 'error.main' : 'text.primary' }}>
              {formatMoney(owed)}
            </Typography>
          </Box>
          <span role="cell" />
          <span role="cell" />
        </Box>
      )}
    </Box>
  );
};

export default ChildrenMonthTable;
